import { DOCUMENT, Injectable, computed, inject, signal } from '@angular/core';

/**
 * The event Chromium fires when the app meets the installability criteria. It is not part of the DOM
 * typings yet, so it is declared here.
 */
export interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

/** How long the install invitation stays hidden after the user dismissed it. */
const DISMISSAL_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const DISMISSAL_STORAGE_KEY = 'pwa.install.dismissedAt';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
    private readonly _document = inject(DOCUMENT);
    private readonly _window = this._document.defaultView;

    private readonly _deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
    private readonly _installed = signal<boolean>(false);
    private readonly _dismissed = signal<boolean>(false);

    /** True when the app runs from the home screen rather than from a browser tab. */
    readonly standalone = signal<boolean>(false);

    /** True on iOS/iPadOS, where installation is a manual "Share > On the home screen" flow. */
    readonly ios = signal<boolean>(false);

    /** True once the app has been installed during this session. */
    readonly installed = this._installed.asReadonly();

    /** True when calling `install()` will open the native installation dialog. */
    readonly canInstall = computed(() => this._deferredPrompt() !== null);

    /**
     * True when the app is worth proposing for installation: it is not installed already, it is not
     * running from the home screen, the user did not dismiss the invitation recently, and either the
     * browser offered us a prompt or we are on iOS where we have to explain the manual steps.
     */
    readonly shouldPromptInstall = computed(
        () =>
            !this.standalone() &&
            !this._installed() &&
            !this._dismissed() &&
            (this.canInstall() || this.ios())
    );

    constructor() {
        if (!this._window) {
            return;
        }

        this.standalone.set(this._detectStandalone());
        this.ios.set(this._detectIos());
        this._dismissed.set(this._isRecentlyDismissed());
        this._reflectStandaloneOnDocument();

        this._window.addEventListener('beforeinstallprompt', (event: Event) => {
            // Keep the browser from showing its own mini-infobar so the invitation is displayed
            // inside the app, where it can be styled and placed in context.
            event.preventDefault();
            this._deferredPrompt.set(event as BeforeInstallPromptEvent);
        });

        this._window.addEventListener('appinstalled', () => {
            this._deferredPrompt.set(null);
            this._installed.set(true);
        });

        // The display mode changes without a reload when the user installs the app from the browser
        // menu and keeps browsing.
        this._window
            .matchMedia('(display-mode: standalone)')
            .addEventListener('change', (event: MediaQueryListEvent) => {
                if (event.matches) {
                    this.standalone.set(true);
                    this._reflectStandaloneOnDocument();
                }
            });
    }

    /**
     * Opens the native installation dialog.
     *
     * @returns `true` when the user accepted the installation, `false` otherwise (including when no
     * prompt was available, which is always the case on iOS).
     */
    async install(): Promise<boolean> {
        const prompt = this._deferredPrompt();

        if (!prompt) {
            return false;
        }

        await prompt.prompt();
        const { outcome } = await prompt.userChoice;

        // The event can only be used once; Chromium fires a new one if the app stays installable.
        this._deferredPrompt.set(null);

        if (outcome === 'accepted') {
            this._installed.set(true);
            return true;
        }

        this.dismiss();
        return false;
    }

    /** Hides the install invitation for the next {@link DISMISSAL_DURATION_MS}. */
    dismiss(): void {
        this._dismissed.set(true);

        try {
            this._window?.localStorage.setItem(DISMISSAL_STORAGE_KEY, String(Date.now()));
        } catch {
            // Private browsing or a full quota: the invitation simply comes back on the next visit.
        }
    }

    /**
     * Exposes the display mode to the stylesheets: an installed app owns the whole screen and has to
     * account for the notch and the home indicator itself, a browser tab does not.
     */
    private _reflectStandaloneOnDocument(): void {
        this._document.documentElement.classList.toggle(
            'pwa-standalone',
            this.standalone()
        );
    }

    private _detectStandalone(): boolean {
        const navigatorWithStandalone = this._window?.navigator as Navigator & {
            standalone?: boolean;
        };

        return (
            this._window?.matchMedia('(display-mode: standalone)').matches === true ||
            this._window?.matchMedia('(display-mode: fullscreen)').matches === true ||
            this._window?.matchMedia('(display-mode: minimal-ui)').matches === true ||
            // iOS does not implement the display-mode media query.
            navigatorWithStandalone?.standalone === true
        );
    }

    private _detectIos(): boolean {
        const navigator = this._window?.navigator;

        if (!navigator) {
            return false;
        }

        const isIPhoneOrIPod = /iphone|ipod/i.test(navigator.userAgent);

        // iPadOS reports itself as a Mac, and is only distinguishable by its touch support.
        const isIPad =
            /ipad/i.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        return isIPhoneOrIPod || isIPad;
    }

    private _isRecentlyDismissed(): boolean {
        try {
            const dismissedAt = Number(
                this._window?.localStorage.getItem(DISMISSAL_STORAGE_KEY)
            );

            return (
                Number.isFinite(dismissedAt) &&
                dismissedAt > 0 &&
                Date.now() - dismissedAt < DISMISSAL_DURATION_MS
            );
        } catch {
            return false;
        }
    }
}
